import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Spinner } from '../components';
import type { Access } from '../routes/manifest';
import { safeReturn } from './safe-return';
import { ACCESS_LEVEL, useSession } from './session-context';
import { ForbiddenContent } from './status-content';

export function FullPageSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas">
      <Spinner size="lg" label="Loading MantisBT…" />
    </div>
  );
}

/**
 * Route access (manifest `access`). Not logged in → /login?return=<this page>; logged in on an
 * anonymous-only page (login, signup) → its `return` page or home; level too low → 403 content in place. The API is the
 * real authority; this only avoids showing pages the user cannot use.
 */
export function RequireAccess({ access, children }: { access: Access; children: ReactNode }) {
  const { state } = useSession();
  const location = useLocation();
  if (access === 'public') return children;
  if (state.status === 'loading') return <FullPageSpinner />;
  // same target as the login page's own redirect, so the two never race to different places
  if (access === 'anonymous-only')
    return state.status === 'authenticated' ? (
      <Navigate to={safeReturn(new URLSearchParams(location.search).get('return'))} replace />
    ) : (
      children
    );
  if (state.status !== 'authenticated') {
    const back = location.pathname + location.search;
    return (
      <Navigate
        to={back === '/' ? '/login' : `/login?return=${encodeURIComponent(back)}`}
        replace
      />
    );
  }
  if (state.user.accessLevel < ACCESS_LEVEL[access]) return <ForbiddenContent />;
  return children;
}
